import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { AxiosError } from 'axios';
import { apiClient } from '../../../utils/axios';
import { useActionDeniedSnackbar } from '../../../hooks/useActionDeniedSnackbar';

export const fetchBulkUploadTemplateConfigs = async (category, subCategory) => {
  const { data } = await apiClient.get('/bulk-uploads/template', {
    params: { category, subCategory },
  });

  return Array.isArray(data?.template_configs) ? data.template_configs : [];
};

const PARTY_PROFILE_CATEGORIES = new Set(['CUSTOMER', 'SUPPLIER', 'PRODUCT']);

const TEMPLATE_CONFIG_REQUESTS = [
  ['E_INVOICE', 'SALES'],
  ['E_INVOICE', 'PURCHASES'],
  ['NON_E_INVOICE', 'SALES'],
  ['NON_E_INVOICE', 'PURCHASES'],
  ['CUSTOMER', undefined],
  ['SUPPLIER', undefined],
  ['PRODUCT', undefined],
];

const fetchTemplateConfigs = async (category, subCategory, silent) => {
  const fetchOne = async (templateCategory, templateSubCategory) => {
    try {
      return await fetchBulkUploadTemplateConfigs(templateCategory, templateSubCategory);
    } catch (error) {
      if (silent) {
        return [];
      }

      throw error;
    }
  };

  if (!category) {
    const configs = await Promise.all(
      TEMPLATE_CONFIG_REQUESTS.map(([templateCategory, templateSubCategory]) =>
        fetchOne(templateCategory, templateSubCategory),
      ),
    );

    return configs.flat();
  }

  if (subCategory || PARTY_PROFILE_CATEGORIES.has(category)) {
    return fetchOne(category, subCategory);
  }

  const [salesConfigs, purchasesConfigs] = await Promise.all([
    fetchOne(category, 'SALES'),
    fetchOne(category, 'PURCHASES'),
  ]);

  return [...salesConfigs, ...purchasesConfigs];
};

export const useGetBulkUploadTemplateConfigsHandler = ({ category, subCategory, silent = false }) => {
  const showActionDeniedSnackbar = useActionDeniedSnackbar();

  const {
    data: templateConfigs = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['bulk_upload_template_configs', category ?? 'ALL', subCategory ?? 'ALL', silent],
    queryFn: () => fetchTemplateConfigs(category, subCategory, silent),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!silent && error instanceof AxiosError && error.response?.status === 403) {
      showActionDeniedSnackbar();
    }
  }, [error, showActionDeniedSnackbar, silent]);

  const getTemplateDisplayName = useCallback(
    (templateName) => {
      if (!templateName) {
        return '';
      }

      const config = templateConfigs.find(
        ({ templateName: configTemplateName }) => configTemplateName === templateName,
      );

      return config?.displayName ?? templateName;
    },
    [templateConfigs],
  );

  return {
    templateConfigs,
    isLoading,
    getTemplateDisplayName,
  };
};
